/** Small, accessible building blocks shared by every page. */
import type { ComponentChildren, JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { useT } from "../i18n";
import { Icon } from "./Icon";

type ButtonProps = JSX.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "signal" | "danger";
  size?: "sm" | "md" | "lg";
  block?: boolean;
  icon?: string;
  children?: ComponentChildren;
};

export function Button({
  variant = "primary",
  size = "md",
  block,
  icon,
  class: cls,
  children,
  ...rest
}: ButtonProps) {
  const classes = [
    "btn",
    variant !== "primary" ? variant : "",
    size !== "md" ? size : "",
    block ? "block" : "",
    cls ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" class={classes} {...rest}>
      {icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}

export function Card({
  class: cls,
  children,
  ...rest
}: JSX.HTMLAttributes<HTMLDivElement> & { children?: ComponentChildren }) {
  return (
    <div class={`card ${cls ?? ""}`} {...rest}>
      {children}
    </div>
  );
}

export function Pill({
  tone,
  children,
  class: cls,
}: {
  tone?: "accent" | "signal" | "live" | "quality";
  children: ComponentChildren;
  class?: string;
}) {
  return <span class={`pill ${tone ?? ""} ${cls ?? ""}`}>{children}</span>;
}

export function QualityPill({ quality }: { quality: "best" | "good" | "risky" | "bad" }) {
  const t = useT();
  const icon =
    quality === "best"
      ? "circle-check"
      : quality === "good"
        ? "check"
        : quality === "risky"
          ? "alert-triangle"
          : "octagon-alert";
  return (
    <span class={`pill quality q-${quality}`}>
      <Icon name={icon} /> {t(`quality.${quality}`)}
    </span>
  );
}

export function Field({
  label,
  help,
  error,
  htmlFor,
  children,
}: {
  label: string;
  help?: string;
  error?: string | null;
  htmlFor?: string;
  children: ComponentChildren;
}) {
  return (
    <div class="field">
      <label for={htmlFor}>{label}</label>
      {children}
      {help ? <div class="help">{help}</div> : null}
      {error ? (
        <div class="error-text" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

export function Toggle({
  label,
  help,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  help?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  const id = useRef(`tg-${Math.random().toString(36).slice(2, 8)}`);
  return (
    <div class="toggle">
      <div>
        <div id={id.current} style={{ fontWeight: 700 }}>
          {label}
        </div>
        {help ? <div class="help muted small">{help}</div> : null}
      </div>
      <button
        type="button"
        class="switch"
        role="switch"
        aria-checked={checked}
        aria-labelledby={id.current}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      />
    </div>
  );
}

export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div class="field">
      <div class="label">{label}</div>
      <div class="segmented" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={o.value === value}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Dialog({
  title,
  onClose,
  children,
  labelledBy,
  footer,
  class: cls,
}: {
  title: string;
  onClose: () => void;
  children: ComponentChildren;
  labelledBy?: string;
  /** Pinned below the scrolling body, outside it — for a dialog whose outer size must not move
   * as its content does (see `.a11y-dialog` in components.css). Omit it and the dialog behaves as
   * before: a single scrolling column under the header. */
  footer?: ComponentChildren;
  /** Extra class on the dialog surface itself, e.g. "a11y-dialog" for a fixed-footprint variant. */
  class?: string;
}) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>("button, [href], input, select, textarea");
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      prev?.focus();
    };
  }, [onClose]);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: click-outside-to-close; keyboard users close with Escape or the Close button
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape is handled on document
    <div class="dialog-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        class={`dialog ${cls ?? ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy ?? "dlg-title"}
        ref={ref}
      >
        <header>
          <h2 id={labelledBy ?? "dlg-title"}>{title}</h2>
          <Button variant="ghost" size="sm" icon="x" aria-label={t("action.close")} onClick={onClose} />
        </header>
        <div class="dialog-body">{children}</div>
        {footer ? <div class="dialog-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

/* ---------- Toasts ---------- */

export interface ToastItem {
  id: number;
  level: "info" | "success" | "warning" | "error";
  text: string;
}

let toastSeq = 0;
const toastListeners = new Set<(t: ToastItem) => void>();

export function toast(text: string, level: ToastItem["level"] = "info"): void {
  const item = { id: ++toastSeq, level, text };
  for (const l of toastListeners) l(item);
}

export function Toasts() {
  const [items, setItems] = useState<ToastItem[]>([]);
  useEffect(() => {
    const l = (t: ToastItem) => {
      setItems((cur) => [...cur.slice(-3), t]);
      setTimeout(
        () => setItems((cur) => cur.filter((x) => x.id !== t.id)),
        t.level === "error" ? 6000 : 3500,
      );
    };
    toastListeners.add(l);
    return () => {
      toastListeners.delete(l);
    };
  }, []);
  return (
    <div class="toasts" aria-live="polite" aria-atomic="false">
      {items.map((t) => (
        <div key={t.id} class={`toast ${t.level}`} role={t.level === "error" ? "alert" : "status"}>
          <Icon
            name={t.level === "error" ? "alert-triangle" : t.level === "success" ? "circle-check" : "info"}
          />
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div class="row" role="status">
      <div class="spinner" aria-hidden="true" />
      {label ? <span>{label}</span> : null}
    </div>
  );
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function CopyButton({ text, label }: { text: string; label?: string }) {
  const t = useT();
  const [done, setDone] = useState(false);
  return (
    <Button
      variant="secondary"
      size="sm"
      icon={done ? "check" : "copy"}
      onClick={async () => {
        if (await copyText(text)) {
          setDone(true);
          setTimeout(() => setDone(false), 1800);
        }
      }}
    >
      {done ? t("action.copied") : (label ?? t("action.copy"))}
    </Button>
  );
}
